/**
 * Permissions management panel for assigning role-based access to users and groups.
 *
 * @module components/admin/Permissions
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React, {Fragment, useEffect, useState,} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {makeStyles} from '@material-ui/core/styles';
import PlayCircleFilledIcon from '@material-ui/icons/PlayCircleFilled';
import LiveHelpIcon from "@material-ui/icons/LiveHelp";
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Grid from '@material-ui/core/Grid';
import {
    Button,
    InputLabel,
    Paper,
    TableContainer,
    TextField,
    Tooltip,
    Typography,
} from '@material-ui/core';
import Checkbox from '@material-ui/core/Checkbox';
import DashboardIcon from '@material-ui/icons/Dashboard';
import PersonIcon from '@material-ui/icons/Person';
import CircularProgress from '@material-ui/core/CircularProgress';
import {fetchPermissionList,} from '../../redux/sims/actions';
import {savePermissionTooltip,} from '../../redux/admin/action';
import AdminsPermissions from '../../permissions/admin';
import {permissionCheck} from '../../utils/permissionCheck';
import PermissionsStyle from './Permissions';


/**
 * Renders the role-based permission matrix, allowing administrators to toggle
 * per-task permissions (view / create / update / delete) for each role and
 * to edit the tooltip/description text for individual permission entries.
 *
 * @param {Function} fetchPermissionList - Action creator that loads the permission list.
 * @param {Object}   permissionList      - The current permission data from the store.
 * @param {Function} handleChange        - Generic change handler passed from the parent.
 * @param {boolean}  refreshPermission   - When truthy, triggers a refresh of the permission list.
 * @param {Function} savePermissionTooltip - Action creator that persists a tooltip update.
 * @param {Array}    roles               - List of role objects to display as columns.
 * @param {Function} editClick           - Callback invoked after a tooltip save completes.
 * @returns {JSX.Element} The permission management table or the tooltip-edit form.
 */
const Permissions = ({
    fetchPermissionList,
    permissionList,
    handleChange,
    refreshPermission,
    savePermissionTooltip,
    roles,
    editClick,
}) => {
    const [roleList, setRoleList] = useState([]);
    const [pDetailCount, setPdetailCount] = useState(1);
    const [loading, setLoading] = useState(true);
    const [edit, setEdit] = useState(false);
    const [currentProduct, setCurrentProduct] = useState('');
    const [state, setState] = useState({
        tooltip: '',
        description: '',
    });
    const useStyles = makeStyles((theme) => PermissionsStyle(theme));
    const classes = useStyles();

    useEffect(() => {
        fetchPermissionList(roles);
    }, [fetchPermissionList, roles]);

    useEffect(() => {
        if (refreshPermission) {
            fetchPermissionList();
        }
    }, [refreshPermission, fetchPermissionList]);

    useEffect(() => {
        if (permissionList && permissionList.roles && permissionList.roles.length && pDetailCount) {
            setRoleList(permissionList.roles);
            setLoading(false);
        }
    }, [permissionList, pDetailCount]);

    /**
     * Toggles a single permission entry in the given role's `permission_details` array.
     * When adding any non-`view` permission, the `view` permission is automatically
     * included if it is not already present.
     *
     * @param {Object} role       - The role whose permissions are being modified.
     * @param {Object} task       - The task object containing the available permissions list.
     * @param {string} permission - The permission type to toggle (e.g. `"view"`, `"create"`).
     */
    const checkboxChange = (role, task, permission) => {
        const perm = task.permissions.find((obj) => obj.permission_type === permission);
        const viewData = task.permissions.find((obj) => obj.permission_type === 'view');

        permissionList.roles.map((list) => {
            if (list._id === role._id) {
                const findIndex = list.permission_details.findIndex((a) => a.permission_id ===
                    perm.permission_id);
                if (findIndex > -1) {
                    list.permission_details.splice(findIndex, 1);
                } else {
                    list.permission_details.push({
                        task_id: task.task_id,
                        task_name: task.task_name,
                        permission_type: perm.permission_type,
                        permission_id: perm.permission_id,
                    });
                    if (viewData
                        &&
                        list.permission_details.findIndex((a) => a.permission_id ===
                            viewData.permission_id) ===
                        -1) {
                        list.permission_details.push({
                            task_id: task.task_id,
                            task_name: task.task_name,
                            permission_type: viewData.permission_type,
                            permission_id: viewData.permission_id,
                        });
                    }
                    if (Object.keys(list.permission_details[0]).length === 0) {
                        list.permission_details.splice(0, 1);
                    }
                }
                if (pDetailCount === list.permission_details.length) {
                    setPdetailCount(pDetailCount + 1);
                } else {
                    setPdetailCount(list.permission_details.length);
                }
            }
        });
    };

    /**
     * Updates the `tooltip` or `description` field in local state based on the
     * input element's `name` attribute.
     *
     * @param {React.ChangeEvent<HTMLInputElement>} evt - The input change event.
     */
    const tooltipHandleChange = (evt) => {
        if (evt.target) {
            const {value} = evt.target;
            setState({
                ...state,
                [evt.target.name]: value,
            });
        }
    };

    /**
     * Persists the current tooltip/description values for `currentProduct`, then
     * resets the edit state and refreshes the permission list.
     *
     * @returns {Promise<void>}
     */
    const saveTooltip = async () => {
        await savePermissionTooltip(state, currentProduct);
        setEdit(false);
        setCurrentProduct('');
        fetchPermissionList();
        editClick();
    };

    /**
     * Renders the task-specific roles with permission checkboxes for view, create, update, and
     * delete actions.
     *
     * @param {Object} task - The task object containing task details and permissions.
     * @param {number} index - The index of the task in the current iteration.
     * @returns {JSX.Element} A table cell with checkboxes for managing role permissions for the
     *     provided task.
     *
     * Each checkbox corresponds to a specific permission type (view, create, update, delete).
     * The checkboxes are conditionally checked based on the task's and role's permission details.
     *
     * Permissions are evaluated by checking if:
     * - The role has permission details.
     * - Permissions exist for the task with the given permission type.
     *
     * The rendered cell applies different styles based on the index for alternating row effects.
     * Clicking on a checkbox triggers permission updates for the corresponding role and task.
     */
    const renderTaskRoles = (task, index) => {
        if (roleList) {
            return roleList.map((role) => {
                let viewChecked = (
                        (role.permission_details?.length &&
                            Object.keys(role.permission_details[0]).length >
                            0) ?
                            role.permission_details?.some(
                                (permission) => (permission?.task_id?.includes(
                                        task.task_id) &&
                                    permission?.permission_type ===
                                    'view')
                            ) :
                            false
                    ),
                    createChecked = (
                        (role.permission_details?.length &&
                            Object.keys(role.permission_details[0]).length >
                            0) ?
                            role.permission_details?.some(
                                (permission) => (permission?.task_id?.includes(
                                        task.task_id) &&
                                    permission?.permission_type ===
                                    'create')
                            ) :
                            false
                    ),
                    updateChecked = (
                        (role.permission_details?.length &&
                            Object.keys(role.permission_details[0]).length >
                            0) ?
                            role.permission_details?.some(
                                (permission) => (permission?.task_id?.includes(
                                        task.task_id) &&
                                    permission?.permission_type ===
                                    'update')
                            ) :
                            false
                    ),
                    deleteChecked = (
                        (role.permission_details?.length &&
                            Object.keys(role.permission_details[0]).length >
                            0) ?
                            role.permission_details?.some(
                                (permission) => (permission?.task_id?.includes(
                                        task.task_id) &&
                                    permission?.permission_type ===
                                    'delete')
                            ) :
                            false
                    );

                return (
                    <TableCell
                        key={role._id}
                        style={{
                            paddingLeft: '8',
                            paddingRight: '8',
                            textAlign: 'center'
                        }}
                        className={(index % 2) ===
                        0 ?
                            classes.eventd :
                            classes.td}
                    >
                        <Grid container
                              className={classes.container}
                              spacing={1}
                        >
                            <Grid item xs={3}>
                                <Checkbox
                                    className={role.permission_details.some(
                                        (permission) => (permission.task_id ===
                                            task.task_id &&
                                            permission.permission_type ===
                                            'view'))
                                        ?
                                        classes.checkBox :
                                        ''}
                                    checked={viewChecked}
                                    color="primary"
                                    onClick={() => handleChange(
                                        role,
                                        task,
                                        'view'
                                    )}
                                    onChange={() => checkboxChange(
                                        role,
                                        task,
                                        'view'
                                    )}
                                    disabled={!(task.permissions.some(
                                        (permission) => permission?.permission_type?.includes(
                                            'view')))}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <Checkbox
                                    className={role.permission_details.some(
                                        (permission) => (permission.task_id ===
                                            task.task_id &&
                                            permission.permission_type ===
                                            'create'))
                                        ?
                                        classes.checkBox :
                                        ''}
                                    onClick={() => handleChange(
                                        role,
                                        task,
                                        'create'
                                    )}
                                    onChange={() => checkboxChange(
                                        role,
                                        task,
                                        'create'
                                    )}
                                    disabled={!(task.permissions.some(
                                        (permission) => permission.permission_type.includes(
                                            'create')))}
                                    checked={createChecked}
                                    color="primary"
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <Checkbox
                                    className={role.permission_details.some(
                                        (permission) => (permission.task_id ===
                                            task.task_id &&
                                            permission.permission_type ===
                                            'update'))
                                        ?
                                        classes.checkBox :
                                        ''}
                                    onClick={() => handleChange(
                                        role,
                                        task,
                                        'update'
                                    )}
                                    onChange={() => checkboxChange(
                                        role,
                                        task,
                                        'update'
                                    )}
                                    checked={updateChecked}
                                    color="primary"
                                    disabled={!(task.permissions.some(
                                        (permission) => permission.permission_type.includes(
                                            'update')))}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <Checkbox
                                    className={role.permission_details.some(
                                        (permission) => (permission.task_id ===
                                            task.task_id &&
                                            permission.permission_type ===
                                            'delete'))
                                        ?
                                        classes.checkBox :
                                        ''}
                                    onClick={() => handleChange(
                                        role,
                                        task,
                                        'delete'
                                    )}
                                    onChange={() => checkboxChange(
                                        role,
                                        task,
                                        'delete'
                                    )}
                                    checked={deleteChecked}
                                    color="primary"
                                    disabled={!(task.permissions.some(
                                        (permission) => permission.permission_type.includes(
                                            'delete')))}
                                />
                            </Grid>
                        </Grid>
                    </TableCell>
                );
            });
        }
    }

    /**
     * Renders a table row for a given task along with its associated data.
     *
     * @param {Object} task - The task object containing information to render in the table row.
     * @param {number} index - The index of the current row, used for styling purposes.
     * @returns {JSX.Element} A TableRow element containing the task details and roles.
     *
     * The rendered table row includes:
     * - A sticky, styled cell displaying the task's name with tooltips showing the task definition.
     * - Additional UI elements for task roles rendered through the `renderTaskRoles` function.
     */
    const renderTaskRow = (task, index) => {
        return (
            <TableRow key={task.task_id}>
                <Tooltip title={task.task_definition}
                         placement="top">
                    <TableCell
                        className={(index % 2) === 0
                            ?
                            classes.even_left :
                            classes.next_left}
                        style={{
                            left: 113,
                            position: 'sticky',
                            zIndex: 2,
                        }}
                    >
                        {' '}
                        {task.task_name}
                    </TableCell>
                </Tooltip>
                {renderTaskRoles(task, index)}
            </TableRow>
        );
    }

    return (
        <>
            {
                !edit
                    ? (
                        loading ? <CircularProgress className={classes.loading} color="primary"/>
                            : (
                                <div className={classes.mainDiv}>
                                    <TableContainer style={{maxHeight: 600}}>
                                        <Table stickyHeader aria-label="sticky table"
                                               className={classes.leftTable}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell
                                                        className={classes.productHead}>PRODUCT</TableCell>
                                                    <TableCell
                                                        className={classes.taskHead}>TASKS</TableCell>
                                                    {permissionList && permissionList.roles
                                                        && permissionList.roles.map((role) => (
                                                            <TableCell key={role._id}
                                                                       className={classes.rolesHead}
                                                                       style={{backgroundColor: '#e1e1e1'}}>
                                                                {role.name}
                                                            </TableCell>
                                                        ))}
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell className={classes.productHead} style={{
                                                        backgroundColor: '#d1d1d1',
                                                        top: 57
                                                    }}/>
                                                    <TableCell className={classes.taskHead} style={{
                                                        backgroundColor: '#d1d1d1',
                                                        top: 57
                                                    }}/>
                                                    {permissionList &&
                                                        permissionList.roles &&
                                                        permissionList.roles.length
                                                        &&
                                                        permissionList.roles.map((role/* , index */) => (
                                                            <TableCell key={role._id}
                                                                       className={classes.rolesHead}
                                                                       style={{top: 57}}>
                                                                <Grid container
                                                                      className={classes.container}>
                                                                    <Grid item
                                                                          xs={3}><span>VIEW</span></Grid>
                                                                    <Grid item
                                                                          xs={3}><span>CREATE</span></Grid>
                                                                    <Grid item
                                                                          xs={3}><span>UPDATE</span></Grid>
                                                                    <Grid item
                                                                          xs={3}><span>DELETE</span></Grid>
                                                                </Grid>
                                                            </TableCell>
                                                        ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {permissionList && permissionList.productTasks
                                                    && permissionList.productTasks.map((row) => (
                                                        <Fragment key={row._id}>
                                                            <TableRow>
                                                                <TableCell rowSpan={row.tasks.length + 1}
                                                                           className={classes.products}>
                                                                    {row.product_name === 'Dashboard' && (
                                                                        <DashboardIcon
                                                                            className={classes.icons}/>
                                                                    )}
                                                                    {row.product_name === 'Admin' && (
                                                                        <PersonIcon
                                                                            className={classes.icons}/>
                                                                    )}
                                                                    {row.product_name === 'Sims' && (
                                                                        <PlayCircleFilledIcon
                                                                            className={classes.icons}/>
                                                                    )}
                                                                    {row.product_name === 'Helpdesk' && (
                                                                        <LiveHelpIcon
                                                                            className={classes.icons}/>
                                                                    )}
                                                                    <br/>
                                                                    <span>
                                                                        <span
                                                                            className={classes.productTitle}>
                                                                            {row.product_name}
                                                                        </span>
                                                                    </span>
                                                                </TableCell>
                                                            </TableRow>
                                                            {row.tasks &&
                                                                row.tasks.map((
                                                                    task,
                                                                    index
                                                                ) => renderTaskRow(task, index))}
                                                        </Fragment>
                                                    ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </div>
                            )
                    )
                    : (
                        <div>
                            <Paper elevation={2} className={classes.paper}>
                                <Typography className={classes.mainTitle}>
                                    Edit Permission Details
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={7}>
                                        <form className={classes.formControl}>
                                            <InputLabel className={classes.initialLabel}>Tool
                                                Tip</InputLabel>
                                            <TextField
                                                variant="outlined"
                                                size="small"
                                                fullWidth
                                                name="tooltip"
                                                value={state.tooltip}
                                                onChange={tooltipHandleChange}
                                            />
                                            <InputLabel
                                                className={classes.label}>Description</InputLabel>
                                            <TextField
                                                className={classes.margin}
                                                fullWidth
                                                name="description"
                                                required
                                                multiline
                                                minRows={20}
                                                variant="outlined"
                                                value={state.description}
                                                onChange={tooltipHandleChange}
                                            />
                                            <div>
                                                <Tooltip
                                                    title={
                                                        !permissionCheck(
                                                            AdminsPermissions.permissions.update.task,
                                                            AdminsPermissions.permissions.update.type,
                                                        )
                                                            ?
                                                            "You don't have permission to create/update Tooltip"
                                                            :
                                                            ''
                                                    }
                                                    placement="right"
                                                >
                          <span>
                            <Button
                                className={permissionCheck(
                                    AdminsPermissions.permissions.update.task,
                                    AdminsPermissions.permissions.update.type,
                                ) ? classes.saveBtn : classes.saveDisableBtn}
                                style={{
                                    marginBottom: '35px',
                                    marginLeft: '16%',
                                }}
                                variant="contained"
                                color="primary"
                                onClick={permissionCheck(
                                    AdminsPermissions.permissions.update.task,
                                    AdminsPermissions.permissions.update.type,
                                ) ? saveTooltip : () => setEdit(true)}
                            >
                              Save
                            </Button>
                          </span>
                                                </Tooltip>
                                                <Button
                                                    variant="outlined"
                                                    className={classes.saveBtn}
                                                    disableElevation
                                                    color="primary"
                                                    style={{
                                                        marginBottom: '35px',
                                                    }}
                                                    onClick={() => {
                                                        setEdit(false);
                                                        editClick();
                                                    }}
                                                >
                                                    CANCEL
                                                </Button>
                                            </div>
                                        </form>
                                    </Grid>
                                </Grid>
                            </Paper>
                        </div>
                    )
            }
        </>
    );
};

/**
 * PropTypes definition for the Permissions component.
 *
 * This specifies the type and shape of props that the Permissions
 * component expects to receive. Typically used for validating
 * component props during development to catch potential bugs.
 *
 * @type {Object}
 */
Permissions.propTypes = {
    fetchPermissionList: PropTypes.func,
    permissionList: PropTypes.object,
    handleChange: PropTypes.func,
    refreshPermission: PropTypes.bool,
    savePermissionTooltip: PropTypes.func,
    roles: PropTypes.array,
    editClick: PropTypes.func,
};

/**
 * @param {Object} state - Redux state.
 * @returns {Object} Props derived from the sim slice of state.
 */
const mapStateToProps = (state) => ({
    permissionList: state.sim.permissionList ? state.sim.permissionList.permissionList : {},
});

/**
 * An object that maps dispatch actions to props for a React-Redux connected component.
 * This allows the component to dispatch the specified actions as functions.
 *
 * @property {Function} fetchPermissionList - Function to dispatch an action to fetch the
 *     permission list.
 * @property {Function} savePermissionTooltip - Function to dispatch an action to save the
 *     permission tooltip state.
 */
const mapDispatchToProps = {
    fetchPermissionList,
    savePermissionTooltip
};

export default connect(mapStateToProps, mapDispatchToProps)(Permissions);
